import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { AgGridReactProps } from 'ag-grid-react';
import {
  GridApi,
  IRowNode,
  themeQuartz,
  Theme,
  iconSetQuartzLight,
} from 'ag-grid-enterprise';
import { PersistedGridState } from './grid/utils/PersistedGridState';
import { loadAGGridModules } from 'src/utils/agGrid/moduleLoader';
import { initializeAgGridLicense } from 'src/utils/agGridLicense';
import Loader from 'src/components/Loader';

/**
 * Generic AG Grid wrapper centralizing theme application & optional localStorage persistence.
 * Provide a persistKey to automatically store & restore column state, filter model,
 * sort model (embedded in column state) and (optionally) expanded group paths.
 */
export interface AGGridProps<TData> extends AgGridReactProps<TData> {
  /** LocalStorage key for persisting user grid state (omit to disable). */
  persistKey?: string;
  /** Persist expanded group paths (joined by '||'). */
  persistExpandedGroups?: boolean;
  /** Debounce ms for persistence writes. */
  persistDebounceMs?: number;
  /** Extra theme params merged with defaults. */
  themeParams?: Record<string, unknown>;
  /** Callback after persisted state restored. */
  onPersistedStateRestored?: (state: PersistedGridState) => void;
  /** Called with GridApi when ready (convenience). */
  onApiReady?: (api: GridApi<TData>) => void;
  /** Provide an externally loaded persisted state (e.g., from API) to apply once. */
  initialPersistedState?: PersistedGridState;
  /** Emit current persistable state after any user-affecting change. */
  onPersistableStateChange?: (state: PersistedGridState) => void;
  /** Automatically select the first visible leaf row (once after data set). */
  selectFirstLeafRow?: boolean;
  /** Show a compact footer beneath the grid with an explicit row count. */
  showRowCountFooter?: boolean;
  /** Loaded row count to render in the compact footer when enabled. */
  footerRowCount?: number;
  /** Total row count to render after "of" in the compact footer. */
  footerTotalRowCount?: number;
}

const DEFAULT_THEME_PARAMS = {
  fontFamily: 'inter',
  cellTextColor: '#202124',
  headerBackgroundColor: '#ffffff',
  oddRowBackgroundColor: '#f4f6fb',
  wrapperBorder: false,
  wrapperBorderRadius: '0px',
  headerFontWeight: 'normal',
  headerTextColor: '#202124bf',
  rowBorder: false,
  backgroundColor: '#ffffff',
  browserColorScheme: 'light',
  headerFontSize: 15,
  sidePanelBorder: false,
  spacing: 8,
  columnBorder: false,
};

export interface AGGridHandle {
  getPersistableState: () => PersistedGridState | null;
  refreshPersistableState: () => void; // forces capture & emit
}

function InnerAGGrid<TData = unknown>(
  props: AGGridProps<TData>,
  ref: React.Ref<AGGridHandle>,
) {
  const {
    persistKey,
    persistExpandedGroups = true,
    persistDebounceMs = 250,
    themeParams,
    onPersistedStateRestored,
    onApiReady,
    initialPersistedState,
    onPersistableStateChange,
    selectFirstLeafRow = false,
    showRowCountFooter = false,
    footerRowCount,
    footerTotalRowCount,
    /** Event handlers we wrap so we must re-expose */
    onColumnMoved,
    onColumnVisible,
    onColumnPinned,
    onFilterChanged,
    onSortChanged,
    onRowGroupOpened,
    onCellValueChanged,
    onGridReady,
    statusBar,
    className,
    ...rest
  } = props;
  const isClientSideRowModel = props.rowModelType
    ? props.rowModelType === 'clientSide'
    : true;

  const [modulesLoaded, setModulesLoaded] = useState(false);
  const apiRef = useRef<GridApi<TData> | null>(null);
  const lastStateRef = useRef<PersistedGridState | null>(null);
  const restoredRef = useRef(false);
  const appliedInitialExternalRef = useRef(false);
  const firstLeafSelectedRef = useRef(false);
  const saveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load AG Grid modules and initialize license on first render
  useEffect(() => {
    Promise.all([initializeAgGridLicense(), loadAGGridModules()])
      .then(() => {
        setModulesLoaded(true);
      })
      .catch((error) => {
        console.error('Failed to load AG Grid modules or license:', error);
      });
  }, []);

  const theme: Theme | 'legacy' = useMemo(
    () =>
      themeQuartz.withPart(iconSetQuartzLight).withParams({
        ...DEFAULT_THEME_PARAMS,
        ...themeParams,
      }),
    [themeParams],
  );

  const resolvedStatusBar = useMemo<AgGridReactProps<TData>['statusBar']>(
    () => statusBar,
    [statusBar],
  );

  const hasRowCountFooter =
    showRowCountFooter && typeof footerRowCount === 'number';

  const collectExpandedGroups = useCallback((): string[] => {
    const api = apiRef.current;
    if (!api) return [];
    const groups: string[] = [];
    api.forEachNode((node) => {
      if (node.group && node.expanded) {
        const path: string[] = [];
        let current: IRowNode | null | undefined = node;
        while (current && current.group) {
          path.unshift(String(current.key));
          current = current.parent;
        }
        groups.push(path.join('||'));
      }
    });
    return groups;
  }, []);

  const buildState = useCallback((): PersistedGridState | null => {
    if (!apiRef.current) return null;
    return {
      columnState: apiRef.current.getColumnState?.(),
      filterModel: apiRef.current.getFilterModel?.(),
      expandedGroups: persistExpandedGroups ? collectExpandedGroups() : [],
    };
  }, [persistExpandedGroups, collectExpandedGroups]);

  const persistState = useCallback(() => {
    if (!apiRef.current) return;
    const state = buildState();
    if (!state) return;
    lastStateRef.current = state;
    if (persistKey) {
      try {
        localStorage.setItem(persistKey, JSON.stringify(state));
      } catch {
        console.log('Failed to persist grid state');
      }
    }
    onPersistableStateChange?.(state);
  }, [persistKey, buildState, onPersistableStateChange]);

  const queuePersist = useCallback(() => {
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(persistState, persistDebounceMs);
  }, [persistState, persistDebounceMs]);
  useImperativeHandle(ref, () => ({
    getPersistableState: () => lastStateRef.current,
    refreshPersistableState: () => persistState(),
  }));

  const restoreState = useCallback(() => {
    if (!persistKey || !apiRef.current) return;
    let parsed: PersistedGridState | null = null;
    try {
      const raw = localStorage.getItem(persistKey);
      if (raw) parsed = JSON.parse(raw);
    } catch {
      console.log('Failed to restore grid state');
    }
    if (!parsed) return;
    try {
      if (parsed.columnState)
        apiRef.current.applyColumnState?.({
          state: parsed.columnState,
          applyOrder: true,
        });
      if (parsed.filterModel)
        apiRef.current.setFilterModel?.(parsed.filterModel);
      if (parsed.expandedGroups && parsed.expandedGroups.length) {
        // Delay expansion until rows rendered
        setTimeout(() => {
          apiRef.current?.forEachNode((node) => {
            if (!node.group) return;
            const path: string[] = [];
            let current: IRowNode | null | undefined = node;
            while (current && current.group) {
              path.unshift(String(current.key));
              current = current.parent;
            }
            if (parsed!.expandedGroups!.includes(path.join('||'))) {
              node.setExpanded(true);
            }
          });
        }, 0);
      }
      onPersistedStateRestored?.(parsed);
    } catch {
      console.log('Failed to restore grid state');
    }
  }, [persistKey, onPersistedStateRestored]);

  // Restore once after first data set + grid ready
  useEffect(() => {
    if (!persistKey) return; // nothing to do
    if (restoredRef.current) return;
    if (!apiRef.current) return;
    restoreState();
    restoredRef.current = true;
  }, [persistKey, restoreState, props.rowData]);

  // Apply externally provided initial state (only once, independent of persistKey)
  useEffect(() => {
    if (!apiRef.current) return;
    if (!initialPersistedState) return;
    if (appliedInitialExternalRef.current) return;
    try {
      if (initialPersistedState.columnState) {
        apiRef.current.applyColumnState?.({
          state: initialPersistedState.columnState,
          applyOrder: true,
        });
      }
      if (initialPersistedState.filterModel) {
        apiRef.current.setFilterModel?.(initialPersistedState.filterModel);
      }
      appliedInitialExternalRef.current = true;
      onPersistedStateRestored?.(initialPersistedState);
    } catch {
      console.log('Failed to apply initial persisted state');
    }
  }, [initialPersistedState, onPersistedStateRestored]);

  // Auto select first leaf row (once) when requested
  useEffect(() => {
    if (!selectFirstLeafRow) return;
    if (!isClientSideRowModel) return;
    if (firstLeafSelectedRef.current) return;
    const api = apiRef.current;
    if (!api) return;
    if (typeof api.forEachNodeAfterFilterAndSort !== 'function') return;
    let firstNode: IRowNode<TData> | null = null;
    api.forEachNodeAfterFilterAndSort((node) => {
      if (!node.group && !firstNode) firstNode = node;
    });
    if (firstNode) {
      api.deselectAll();
      // Cast to allow selecting; AG Grid's IRowNode includes setSelected at runtime.
      (
        firstNode as unknown as { setSelected: (v: boolean) => void }
      ).setSelected(true);
      api.ensureNodeVisible(firstNode, 'middle');
      firstLeafSelectedRef.current = true;
    }
  }, [isClientSideRowModel, selectFirstLeafRow, props.rowData]);

  // Cleanup timeout
  useEffect(() => {
    return () => {
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
      }
    };
  }, []);

  // Wrapper event helpers (invoke user handler then persist queue)
  // Generic wrapper preserving the original handler's argument tuple (no usage of `any`).
  const wrap = <Args extends unknown[]>(
    user?: (...args: Args) => unknown,
    also?: () => void,
  ) =>
    ((...args: Args) => {
      user?.(...args);
      also?.();
    }) as (...args: Args) => unknown;

  // Show loading state while modules are being loaded
  if (!modulesLoaded) {
    return <Loader />;
  }

  const gridElement = (
    <AgGridReact
      theme={theme}
      onGridReady={(params) => {
        apiRef.current = params.api as GridApi<TData>;
        onGridReady?.(params);
        onApiReady?.(params.api as GridApi<TData>);
        // Attempt restore if data already present
        if (persistKey && !restoredRef.current) {
          restoreState();
          restoredRef.current = true;
        }
      }}
      onColumnMoved={wrap(onColumnMoved, queuePersist)}
      onColumnVisible={wrap(onColumnVisible, queuePersist)}
      onColumnPinned={wrap(onColumnPinned, queuePersist)}
      onFilterChanged={wrap(onFilterChanged, queuePersist)}
      onSortChanged={wrap(onSortChanged, queuePersist)}
      onRowGroupOpened={wrap(onRowGroupOpened, queuePersist)}
      onCellValueChanged={wrap(onCellValueChanged, queuePersist)}
      statusBar={resolvedStatusBar}
      className={
        hasRowCountFooter
          ? ['flex-1 min-h-0', className].filter(Boolean).join(' ')
          : className
      }
      {...rest}
    />
  );

  if (!hasRowCountFooter) {
    return gridElement;
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {gridElement}
      <div className="flex items-center justify-end border-t border-gray-100 bg-white pt-4 px-3 text-sm font-medium text-gray-600">
        <span className="text-right">
          Rows : {footerRowCount}
          {typeof footerTotalRowCount === 'number'
            ? ` of ${footerTotalRowCount}`
            : ''}
        </span>
      </div>
    </div>
  );
}

export type AGGridComponent = <TData = unknown>(
  props: AGGridProps<TData> & { ref?: React.Ref<AGGridHandle> },
) => React.ReactElement | null;

const AGGrid = forwardRef(InnerAGGrid) as AGGridComponent;

export default AGGrid;
